/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useMemo, useState } from 'react'

import { EuiBadge, EuiFlexGroup, EuiFlexItem, EuiPopover, useEuiTheme } from '@elastic/eui'

import type { AnyProject } from '@modules/project-api/types'
import TagBadge from '@modules/resources-shared-components/Tags/TagBadge'
import type { Tag } from '@modules/resources-shared-components/Tags/types'

const convertProjectTagsToTagArray = (projectTags?: Record<string, string>): Tag[] => {
  if (!projectTags) {
    return []
  }

  return Object.entries(projectTags).map(([key, value]) => ({ key, value }))
}

type Props = {
  project: AnyProject
}

const ProjectTagsCell: React.FC<Props> = ({ project }) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const { euiTheme } = useEuiTheme()
  const tags = useMemo(
    () => convertProjectTagsToTagArray(project.metadata.tags),
    [project.metadata.tags],
  )

  if (tags.length === 0) {
    return null
  }

  if (tags.length === 1) {
    return <TagBadge tag={tags[0]!} />
  }

  const firstTag = tags[0]!
  const remainingTags = tags.slice(1)

  return (
    <EuiFlexGroup gutterSize='s' alignItems='center' wrap={false}>
      <EuiFlexItem grow={false} style={{ maxWidth: '200px' }}>
        <TagBadge tag={firstTag} />
      </EuiFlexItem>
      <EuiFlexItem grow={false}>
        <EuiPopover
          button={
            <EuiBadge
              color='hollow'
              onClick={() => setIsPopoverOpen(!isPopoverOpen)}
              onClickAriaLabel={`Show ${remainingTags.length} more tags`}
              style={{ cursor: 'pointer' }}
              data-test-subj='cps-projectTagsCell-showMoreTags'
            >
              +{remainingTags.length}
            </EuiBadge>
          }
          isOpen={isPopoverOpen}
          closePopover={() => setIsPopoverOpen(false)}
          anchorPosition='downRight'
          aria-label={`Show ${remainingTags.length} more tags`}
        >
          <div style={{ maxWidth: '300px', padding: euiTheme.size.s }}>
            <EuiFlexGroup gutterSize='s' wrap={true}>
              {remainingTags.map((tag, index) => (
                <EuiFlexItem key={`${tag.key}-${tag.value}-${index}`} style={{ maxWidth: '200px' }}>
                  <TagBadge tag={tag} />
                </EuiFlexItem>
              ))}
            </EuiFlexGroup>
          </div>
        </EuiPopover>
      </EuiFlexItem>
    </EuiFlexGroup>
  )
}

export default ProjectTagsCell
